import React, { useEffect, useState } from 'react';
import { FaMoon, FaSun, FaDumbbell, FaUtensils, FaFireAlt } from 'react-icons/fa';
import { generateWorkoutPdf } from './utils/pdfGenerator';
import { generateNutritionPdf } from './utils/pdfNutrition';
import { Tab } from '@headlessui/react';
import VideoPlayer from './VideoPlayer';

export default function ActivePlan() {
  const [dark, setDark] = useState(true);

  // تمرین
  const [workoutDays, setWorkoutDays] = useState([]); // قبلاً plan
  const [warmup, setWarmup] = useState([]); // سکشن گرم کردن که به همه روزها مربوطه

  // تغذیه
  const [nutrition, setNutrition] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL;
  const planId = localStorage.getItem('planId');

  const weekDayMap = {
    Saturday: 'شنبه',
    Sunday: 'یکشنبه',
    Monday: 'دوشنبه',
    Tuesday: 'سه‌شنبه',
    Wednesday: 'چهارشنبه',
    Thursday: 'پنجشنبه',
    Friday: 'جمعه'
  };

  const mealTypeMap = {
    Breakfast: 'صبحانه',
    MorningSnack: 'میان‌وعده صبح',
    Lunch: 'ناهار',
    AfternoonSnack: 'میان‌وعده عصر',
    Dinner: 'شام',
    EveningSnack: 'میان‌وعده غروب',
    NightSnack: 'میان‌وعده شب',
    Snacks: 'میان‌وعده شب'
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        };

        const [workoutRes, nutritionRes] = await Promise.all([
          fetch(`https://api.mogym.ir/api/plan/${planId}/workout`, { headers }),
          fetch(`https://api.mogym.ir/api/plan/${planId}/meal`, { headers })
        ]);

        if (!workoutRes.ok || !nutritionRes.ok) throw new Error();

        const workoutData = await workoutRes.json();
        const nutritionData = await nutritionRes.json();

        // ورژن جدید بک‌اند تمرین:
        // {
        //   WorkoutDetails: [...روزهای تمرین...],
        //   Warmup: [...حرکات گرم کردن ساده...]
        // }

        setWorkoutDays(workoutData.WorkoutDetails || []);
        setWarmup(Array.isArray(workoutData.Warmup) ? workoutData.Warmup : []);

        // تغذیه مثل قبل
        setNutrition(nutritionData.Result || []);
      } catch (err) {
        setError('مشکلی در دریافت برنامه رخ داد.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [planId]);

  // -----------------------
  // سکشن تمرینی (ورک‌اوت)
  // -----------------------
  const renderWorkout = () => {
    if (
      (!workoutDays || !Array.isArray(workoutDays) || workoutDays.length === 0) &&
      (!warmup || warmup.length === 0)
    ) {
      return <p>📭 برنامه تمرینی پیدا نشد.</p>;
    }

    return (
      <div className="space-y-6">
        {/* دکمه دانلود PDF */}
        <button
          onClick={() => generateWorkoutPdf({ warmup, days: workoutDays })}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl"
        >
          دانلود برنامه تمرینی (PDF)
        </button>

        {/* 🔥 حرکات گرم کردن */}
        {warmup.length > 0 && (
          <div className="bg-orange-50 dark:bg-[#2b1f12] p-5 rounded-xl shadow border border-orange-200 dark:border-orange-700">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2 text-orange-700 dark:text-orange-400">
              <FaFireAlt className="text-orange-500 dark:text-orange-400" />
              حرکات گرم کردن
            </h2>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
              این حرکات رو قبل از شروع هر روز تمرین انجام بده 👇
            </p>

            <ol className="space-y-3 list-decimal pr-5 marker:text-orange-600 dark:marker:text-orange-300 text-right">
              {warmup
                .slice()
                .sort((a, b) => {
                  const A = parseInt(a.Order, 10);
                  const B = parseInt(b.Order, 10);
                  if (isNaN(A) || isNaN(B)) return 0;
                  return A - B;
                })
                .map((item, idx) => (
                  <li
                    key={item.Id ?? idx}
                    className="bg-orange-100 dark:bg-[#4a3117] p-3 rounded-lg shadow-sm border border-orange-200 dark:border-orange-800"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center justify-between flex-wrap">
                        <span className="font-bold text-[15px] text-orange-900 dark:text-orange-200 flex items-center gap-2">
                          <FaDumbbell className="text-orange-600 dark:text-orange-300" />
                          {item.Name}
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                          {item.SetOrDuration}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
            </ol>
          </div>
        )}

        {/* 🏋️ روزهای تمرینی اصلی */}
        {workoutDays && workoutDays.length > 0 && workoutDays.map((day, idx) => (
          <div key={idx} className="bg-gray-50 dark:bg-[#1c1f2b] p-5 rounded-xl shadow">
            <h2 className="text-xl font-bold mb-3">
              🗓 {day.DayOfWeek} - عضله هدف: {day.MuscleGroup}
            </h2>

            {day.WorkoutGroups?.map((group, gIdx) => (
              <div key={gIdx} className="mb-4 border-t border-gray-300 pt-4">
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                  <FaDumbbell className="text-blue-500" />
                  {group.Type === 'Superset'
                    ? 'سوپرست'
                    : group.Type === 'Dropset'
                    ? 'دراپ‌ست'
                    : 'تمرین'}{' '}
                  #{group.GroupId}
                </h3>

                {group.ExerciseRecords?.map((ex, i) => (
                  <div
                    key={i}
                    className="bg-blue-50 dark:bg-[#2a2f3b] p-3 rounded-xl mb-3"
                  >
                    <p className="font-bold">🔹 {ex.Name}</p>
                    <p className="text-sm">
                      {ex.Sets} ست × {ex.Reps} تکرار
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      🕒 استراحت: {ex.RestSeconds} ثانیه
                    </p>

                    <VideoPlayer
                      url={ex.VideoUrl}
                      title={ex.Name}
                      exerciseId={ex.ExerciseId}
                    />

                    <p className="text-sm mt-1">{ex.Description}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // -----------------------
  // سکشن تغذیه
  // -----------------------
  const renderNutrition = () => {
    const result = nutrition;

    if (
      !result ||
      !result.Days ||
      !Array.isArray(result.Days) ||
      result.Days.length === 0
    ) {
      return <p>📭 برنامه تغذیه‌ای پیدا نشد.</p>;
    }

    return (
      <div className="space-y-6">
        <div className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 p-4 rounded-xl">
          <p>کالری هدف: {result.TotalCalories} kcal</p>
          <p>
            پروتئین: {result.TotalProteinGrams}g | کربوهیدرات: {result.TotalCarbsGrams}g | چربی:{' '}
            {result.TotalFatGrams}g
          </p>
        </div>

        <button
          onClick={() => generateNutritionPdf(result)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl"
        >
          دانلود برنامه تغذیه‌ای (PDF)
        </button>

        {result.Days.map((dayItem, idx) => (
          <div
            key={idx}
            className="bg-purple-50 dark:bg-[#2f2b3f] p-5 rounded-xl shadow"
          >
            <h2 className="text-lg font-bold mb-4">
              📅 {weekDayMap[dayItem.Day]}
            </h2>

            {dayItem.Meals.map((meal, mealIdx) => (
              <div key={mealIdx} className="mb-6">
                <h3 className="font-semibold mb-2 text-purple-700 dark:text-purple-200">
                  🍽 {mealTypeMap[meal.MealType]}
                </h3>

                {meal.Options.map((option, optionIdx) => (
                  <div key={optionIdx} className="pl-4 mb-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      گزینه {optionIdx + 1}:
                    </p>
                    <ul className="list-disc pr-5 text-sm text-gray-700 dark:text-gray-200">
                      {option.Items.map((item, itemIdx) => (
                        <li key={itemIdx}>
                          {item.Name} - {item.Quantity}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {meal.Supplements.length > 0 && (
                  <div className="mt-2 pl-4">
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">
                      💊 مکمل:
                    </p>
                    <ul className="list-disc pr-5 text-sm text-gray-700 dark:text-gray-200">
                      {meal.Supplements.map((supp, sIdx) => (
                        <li key={sIdx}>
                          {supp.Name} - {supp.Quantity}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // -----------------------
  // رندر اصلی صفحه
  // -----------------------
  return (
    <div className={dark ? 'dark' : ''}>
      <div className="min-h-screen bg-white dark:bg-[#0e1015] text-black dark:text-white px-4 py-8 font-vazir">
        <div className="max-w-3xl mx-auto space-y-6">
          <h1 className="text-2xl font-bold text-center mb-6">برنامه فعال شما</h1>

          <div className="text-sm text-gray-600 dark:text-gray-400 text-center mb-4">
            با یک‌بار دانلود، از همین صفحه در حالت آفلاین نیز می‌توانید استفاده کنید.
          </div>

          <div className="flex justify-center mb-6">
            <Tab.Group selectedIndex={tabIndex} onChange={setTabIndex}>
              <Tab.List className="flex gap-4 bg-gray-200 dark:bg-gray-700 p-1 rounded-xl">
                <Tab
                  className={({ selected }) =>
                    selected
                      ? 'bg-blue-600 text-white px-4 py-2 rounded-xl'
                      : 'px-4 py-2 rounded-xl text-gray-700 dark:text-gray-300'
                  }
                >
                  برنامه تمرینی
                </Tab>
                <Tab
                  className={({ selected }) =>
                    selected
                      ? 'bg-purple-600 text-white px-4 py-2 rounded-xl'
                      : 'px-4 py-2 rounded-xl text-gray-700 dark:text-gray-300'
                  }
                >
                  برنامه تغذیه‌ای
                </Tab>
              </Tab.List>

              <Tab.Panels className="mt-6">
                <Tab.Panel>
                  {loading ? (
                    <p>در حال بارگذاری...</p>
                  ) : error ? (
                    <p>{error}</p>
                  ) : (
                    renderWorkout()
                  )}
                </Tab.Panel>

                <Tab.Panel>
                  {loading ? (
                    <p>در حال بارگذاری...</p>
                  ) : error ? (
                    <p>{error}</p>
                  ) : (
                    renderNutrition()
                  )}
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>

        {/* دکمه تغییر تم */}
        <button
          onClick={() => setDark(!dark)}
          className="fixed top-4 right-4 z-50 px-3 py-2 bg-gray-700 text-white rounded-full shadow-lg"
        >
          {dark ? (
            <FaSun className="text-yellow-400" />
          ) : (
            <FaMoon className="text-blue-500" />
          )}
        </button>
      </div>
    </div>
  );
}
